// World-region generator (B2 core, B3 regions — monorail-x8w / ciy). The
// whole world is one contiguous web of basins and halls (epic decision #1);
// the descent gradient IS the room vocabulary (decision #2): huge porous
// basins around the hub — banks full of throats, plays close to the shipped
// open water — tightening band by band until the deep end is corridors
// between small chambers.
//
// B3 structure: the web is TWO clusters. Bands 0–2 are the Shallow Pool;
// bands 3–6 are the deep region, reachable only through a single long
// narrow THROAT — committing to the descent is a legible moment, and the
// deep cluster grows outward on the throat's bearing (a place on the map,
// not a ring around home). One SHORTCUT hall connects the deep cluster
// back toward home, sealed by a door that opens permanently from the deep
// side (the generated Dark Souls loop; state persists in the save).
//
// Depth is GRAPH distance: Dijkstra from the hub (shortcut excluded — an
// escape route home doesn't make a room shallower), band means anchored to
// the biome ladder. depth-field.ts turns that into a per-point query that
// drop-in replaces radial distance-from-origin.

import { mulberry32, floatIn, intIn } from '../rng'
import { T } from '../tunables'
import { relaxPositions, countEdgeCrossings, segmentsIntersect } from './embed'
import { carveLayout } from './carve'
import { isPocketRole } from './types'
import type { DoorKind, DunEdge, DunGraph, DunNode, DungeonLayout, Vec2 } from './types'

interface BandSpec {
  /** Node count (center value; middle bands jitter ±1). */
  n: number
  size: [number, number] // sizeHint range
  /** Chance of extra connections (2nd parent, intra-band pores). */
  porosity: number
  /** Hall width multiplier for edges born in this band. */
  hallW: number
}

// Band 0 is the hub. sizeHint 3.2 × roomBaseR 1.6 ≈ a 5-unit basin — the
// shipped hub ring (radius 4) fits inside with shoreline to spare. hallW
// multiplies T.dungeon.hallHalfWidth (0.55): near-hub throats carve ~1.4
// half-width — banks with bites out of them, not doorways — and tighten to
// corridor weight by the deep end.
const BANDS: BandSpec[] = [
  { n: 1, size: [3.15, 3.3], porosity: 1.0, hallW: 2.6 },
  { n: 5, size: [2.2, 2.9], porosity: 0.9, hallW: 2.2 },
  { n: 6, size: [1.8, 2.4], porosity: 0.7, hallW: 1.8 },
  { n: 7, size: [1.4, 1.9], porosity: 0.5, hallW: 1.4 },
  { n: 7, size: [1.1, 1.5], porosity: 0.35, hallW: 1.1 },
  { n: 6, size: [0.85, 1.2], porosity: 0.28, hallW: 0.9 },
  { n: 5, size: [0.65, 0.95], porosity: 0.2, hallW: 0.75 },
]

// Ladder mapping (raw graph depth → virtual-depth units). Anchored on
// QUANTILES of raw depth, not band indices: since B3's sector structure a
// band-3 chain can run deeper than a band-4 entry, band means aren't
// monotone — but quantiles always are. The shallow→deep anchor is pinned to
// the shallow CLUSTER's share of nodes, so crossing the throat crosses the
// biome line within a room or two — the second region reads deep from its
// doorstep, and the top ~15% of rooms land past the mushroom line.
const DEPTH_TARGETS = [0, 23, 43, 49]

/** Landmark depths as fractions of the far anchor — matches the shipped
 *  radial placements (8, 15, 26 of ~47). */
const LANDMARK_DEPTH_FRACS = [0.17, 0.32, 0.55]

export function generateWorldRegion(seed: number): DungeonLayout {
  // Same crossing-minimizing retry as the mission embedder — tangles are
  // rarer here (the band prior keeps rings concentric) but not impossible.
  let best: DungeonLayout | null = null
  let bestCrossings = Infinity
  for (let attempt = 0; attempt < 6; attempt++) {
    const layout = generateOnce(seed, attempt)
    const crossings = countEdgeCrossings(layout.graph, layout.pos)
    if (crossings < bestCrossings) {
      best = layout
      bestCrossings = crossings
    }
    if (crossings === 0) break
  }
  return best!
}

function generateOnce(seed: number, attempt: number): DungeonLayout {
  const rng = mulberry32((seed ^ 0xc2b2ae35 ^ (attempt * 0x27d4eb2f)) >>> 0)

  const nodes: DunNode[] = []
  const edges: DunEdge[] = []
  const addNode = (n: Omit<DunNode, 'id'>): number => {
    const id = nodes.length
    nodes.push({ id, ...n })
    return id
  }

  // ── Nodes per band, angles inherited from a parent below ──────────────
  // A child sits near its parent's bearing (± a modest jitter) instead of
  // on a uniform ring: outer rings have far more circumference than nodes,
  // and uniform spacing there produced 15-unit diagonal halls — the
  // corridor-tedium trap. Parent-bearing children keep every hall around
  // its rest length and grow radial branches; intra-band pores loop them.
  const bandIds: number[][] = []
  const bandAngle: number[][] = []
  const parentOf = new Map<number, number>()
  for (let b = 0; b < BANDS.length; b++) {
    const spec = BANDS[b]
    const count = b === 0 ? 1 : Math.max(2, spec.n + intIn(rng, -1, 1))
    const ids: number[] = []
    const angles: number[] = []
    const rot = rng() * Math.PI * 2
    for (let i = 0; i < count; i++) {
      const id = addNode({
        role: b === 0 ? 'entrance' : 'room',
        arc: 'side',
        sizeHint: floatIn(rng, spec.size[0], spec.size[1]),
        band: b,
      })
      ids.push(id)
      if (b === 0) {
        angles.push(rot)
      } else if (b === 1) {
        // First ring fans evenly around the hub.
        angles.push(rot + (i / count) * Math.PI * 2 + (rng() - 0.5) * (Math.PI / count) * 0.8)
        parentOf.set(id, bandIds[0][0])
      } else if (b === 3) {
        // Region boundary (B3): band 3 hangs off ONE band-2 node through
        // the throat. The first node is the throat's far mouth; the rest
        // fan off it (or each other) so the deep cluster grows as a sector
        // on the throat's bearing instead of ringing the shallow pool.
        if (i === 0) {
          const tp = intIn(rng, 0, bandIds[2].length - 1)
          parentOf.set(id, bandIds[2][tp])
          angles.push(bandAngle[2][tp])
        } else {
          const prior = intIn(rng, Math.max(0, i - 2), i - 1)
          parentOf.set(id, ids[prior])
          const spread = (i % 2 === 0 ? 1 : -1) * (0.3 + 0.24 * Math.floor(i / 2))
          angles.push(angles[0] + spread + (rng() - 0.5) * 0.2)
        }
      } else {
        // Deterministic proportional parent pick, bearing inherited.
        const parents = bandIds[b - 1]
        const j = Math.floor((i * parents.length) / count)
        parentOf.set(id, parents[j])
        angles.push(bandAngle[b - 1][j] + (rng() - 0.5) * (Math.PI * 2 / Math.max(count, 6)))
      }
    }
    bandIds.push(ids)
    bandAngle.push(angles)
  }
  const hub = bandIds[0][0]

  // ── Edges ─────────────────────────────────────────────────────────────
  // Spanning parent halls (by construction), plus porosity: second-parent
  // links and intra-band pores — the loops that make swimming near the hub
  // feel like open water with banks, not a maze.
  const angDist = (a: number, b: number): number => {
    const d = Math.abs(a - b) % (Math.PI * 2)
    return d > Math.PI ? Math.PI * 2 - d : d
  }
  for (let b = 1; b < bandIds.length; b++) {
    const spec = BANDS[b]
    const hallW = () => (spec.hallW + BANDS[b - 1].hallW) / 2 * floatIn(rng, 0.9, 1.1)
    for (let i = 0; i < bandIds[b].length; i++) {
      const id = bandIds[b][i]
      const parent = parentOf.get(id)!
      // The single band-2→3 link is THE throat: long, narrow, unmistakable.
      const isThroat = b === 3 && nodes[parent].band === 2
      edges.push(isThroat
        ? { a: parent, b: id, kind: 'throat', widthMult: 0.7, restMult: 2.5 }
        : { a: parent, b: id, kind: 'hall', widthMult: hallW() })
      // Second parent: nearest-by-angle other than the primary, and only
      // when it's angularly close enough to carve a sane-length hall.
      // Never across the region boundary (b=3) — the throat stays singular.
      if (b !== 3 && rng() < spec.porosity * 0.6) {
        const second = bandIds[b - 1]
          .map((pid, j) => ({ pid, d: angDist(bandAngle[b][i], bandAngle[b - 1][j]) }))
          .filter(p => p.pid !== parent)
          .sort((u, v) => u.d - v.d)[0]
        if (second && second.d < Math.PI / 2) {
          edges.push({ a: second.pid, b: id, kind: 'hall', widthMult: hallW() })
        }
      }
    }
    // Intra-band pores between angular neighbors — skip pairs that drifted
    // too far apart around the ring (their pore would be a highway).
    const ids = bandIds[b]
    if (ids.length >= 3) {
      const order = ids.map((id, i) => ({ id, ang: bandAngle[b][i] })).sort((u, v) => u.ang - v.ang)
      for (let i = 0; i < order.length; i++) {
        const u = order[i]
        const v = order[(i + 1) % order.length]
        if (angDist(u.ang, v.ang) > Math.PI * 0.6) continue
        if (rng() < spec.porosity) {
          edges.push({ a: u.id, b: v.id, kind: 'hall', widthMult: spec.hallW * floatIn(rng, 0.9, 1.1) })
        }
      }
    }
  }

  // Dead-end pockets. Exactly three 'reward' vaults in the deep cluster —
  // B4 seals them behind gates (two adaptation seals + the hall-pass vault).
  for (let k = 0; k < 3; k++) {
    const b = intIn(rng, 4, BANDS.length - 1)
    const host = bandIds[b][intIn(rng, 0, bandIds[b].length - 1)]
    const p = addNode({ role: 'reward', arc: 'side', sizeHint: floatIn(rng, 0.55, 0.8), band: b })
    edges.push({ a: host, b: p, kind: 'hall', widthMult: 0.8 })
  }
  // Ungated 'treasure' pockets (B6): one on the shallow side plus one per
  // mid/deep band — open halls, guaranteed once-per-save loot visible from
  // the doorway. Every band of the descent keeps offering a legible detour
  // (research: a visible, categorizable promise converts scenery into
  // intent; pockets are pinch-off regions of the same space, no gate).
  for (const b of [2, 3, 4, 5]) {
    const host = bandIds[b][intIn(rng, 0, bandIds[b].length - 1)]
    const p = addNode({ role: 'treasure', arc: 'side', sizeHint: floatIn(rng, 0.55, 0.8), band: b })
    edges.push({ a: host, b: p, kind: 'hall', widthMult: 0.8 })
  }

  const graph: DunGraph = {
    seed, cycleType: 'two-routes', nodes, edges,
    entrance: hub, goal: hub, arcA: [], arcB: [],
  }

  // ── Embed: concentric-band init + radial prior, hub pinned at origin ──
  const bandRadius: number[] = [0]
  for (let b = 1; b < BANDS.length; b++) {
    const rPrev = ((BANDS[b - 1].size[0] + BANDS[b - 1].size[1]) / 2) * T.dungeon.roomBaseR
    const rCur = ((BANDS[b].size[0] + BANDS[b].size[1]) / 2) * T.dungeon.roomBaseR
    bandRadius.push(bandRadius[b - 1] + rPrev + rCur + T.dungeon.hallLength)
  }
  // The throat's extra rest length pushes the whole deep cluster outward.
  for (let b = 3; b < bandRadius.length; b++) {
    bandRadius[b] += T.dungeon.hallLength * 1.5
  }
  const pos: Vec2[] = nodes.map(() => ({ x: 0, y: 0 }))
  for (let b = 0; b < bandIds.length; b++) {
    for (let i = 0; i < bandIds[b].length; i++) {
      const r = bandRadius[b] * floatIn(rng, 0.92, 1.08)
      const a = bandAngle[b][i]
      pos[bandIds[b][i]] = { x: Math.cos(a) * r, y: Math.sin(a) * r }
    }
  }
  for (const node of nodes) {
    if (!isPocketRole(node.role)) continue
    const anchor = edges.find(e => e.b === node.id)!
    const ap = pos[anchor.a]
    const ang = rng() * Math.PI * 2
    pos[node.id] = { x: ap.x + Math.cos(ang) * 3, y: ap.y + Math.sin(ang) * 3 }
  }

  relaxPositions(graph, pos, rng, {
    pin: hub,
    // Weak pull toward the band radius keeps the rings concentric — depth
    // then reads on the map (deeper = farther out) and the web can't fold
    // a deep chamber up against the hub shoreline.
    extraForce: (i, out) => {
      const band = nodes[i].band
      if (band === undefined || band === 0) return
      const p = pos[i]
      const d = Math.hypot(p.x, p.y) || 1e-6
      const pull = (bandRadius[band] - d) * 0.12
      out.x = (p.x / d) * pull
      out.y = (p.y / d) * pull
    },
  })
  // Hub pinned at (0,0) — the world origin stays the spawn point.

  // ── Shortcut (B3): one hall from the deep cluster back toward home,
  // sealed by a door until opened from the deep side. Chosen AFTER the
  // embed settles: the closest deep-room/shallow-room pair whose straight
  // hall doesn't cross existing geometry — the door you notice on the way
  // down and earn on the way back.
  {
    const deepSide = nodes.filter(n => (n.band ?? 0) >= 4 && n.role === 'room')
    const nearHome = (maxBand: number) =>
      nodes.filter(n => (n.band ?? 9) <= maxBand && !isPocketRole(n.role))
    const best = { s: -1, d: -1, dist: Infinity }
    for (const maxBand of [1, 2]) {
      for (const dn of deepSide) {
        for (const sn of nearHome(maxBand)) {
          const dist = Math.hypot(pos[dn.id].x - pos[sn.id].x, pos[dn.id].y - pos[sn.id].y)
          if (dist >= best.dist) continue
          const crosses = edges.some(e => {
            if (e.a === dn.id || e.b === dn.id || e.a === sn.id || e.b === sn.id) return false
            return segmentsIntersect(pos[dn.id], pos[sn.id], pos[e.a], pos[e.b])
          })
          if (!crosses) {
            best.s = sn.id
            best.d = dn.id
            best.dist = dist
          }
        }
      }
      if (best.s >= 0) break // prefer a shortcut straight home (band ≤ 1)
    }
    if (best.s >= 0) {
      edges.push({ a: best.s, b: best.d, kind: 'shortcut', widthMult: 0.55 })
    }
  }

  const layout = carveLayout(graph, pos)

  // ── Graph depth: Dijkstra from the hub over embedded hall lengths,
  // mapped piecewise-linearly through raw-depth quantile anchors onto the
  // ladder. Monotone in true graph distance by construction, whatever the
  // embedding or the band structure did.
  const raw = dijkstraFromHub(graph, pos, hub)
  const sortedRaw = [...raw].sort((a, b) => a - b)
  const qShallow = nodes.filter(n => (n.band ?? 9) <= 2).length / nodes.length
  const quantiles = [0, qShallow, qShallow + (1 - qShallow) * 0.65, 1]
  const anchors = quantiles.map(f =>
    sortedRaw[Math.min(sortedRaw.length - 1, Math.round(f * (sortedRaw.length - 1)))])
  for (let i = 1; i < anchors.length; i++) {
    anchors[i] = Math.max(anchors[i], anchors[i - 1] + 1)
  }
  const mapDepth = (d: number): number => {
    if (d <= anchors[0]) return DEPTH_TARGETS[0]
    for (let i = 1; i < anchors.length; i++) {
      if (d <= anchors[i]) {
        const t = (d - anchors[i - 1]) / (anchors[i] - anchors[i - 1])
        return DEPTH_TARGETS[i - 1] + (DEPTH_TARGETS[i] - DEPTH_TARGETS[i - 1]) * t
      }
    }
    const last = anchors.length - 1
    const slope = (DEPTH_TARGETS[last] - DEPTH_TARGETS[last - 1]) / (anchors[last] - anchors[last - 1])
    return DEPTH_TARGETS[last] + (d - anchors[last]) * slope
  }
  layout.nodeDepth = raw.map(mapDepth)
  layout.hubNode = hub

  // ── Landmark nodes: nearest non-hub, non-pocket room to each target
  // depth, all distinct — same navigation-anchor spread as the shipped
  // radial placements.
  const landmarkNodes: number[] = []
  const farAnchor = DEPTH_TARGETS[DEPTH_TARGETS.length - 1]
  for (const frac of LANDMARK_DEPTH_FRACS) {
    const want = frac * farAnchor
    let bestId = -1
    let bestErr = Infinity
    for (const node of nodes) {
      if (node.id === hub || isPocketRole(node.role) || landmarkNodes.includes(node.id)) continue
      const err = Math.abs(layout.nodeDepth[node.id] - want)
      if (err < bestErr) {
        bestErr = err
        bestId = node.id
      }
    }
    landmarkNodes.push(bestId)
  }
  layout.landmarkNodes = landmarkNodes

  // ── Door plugs. Each seals a hall at its midpoint; plug radius covers
  // the carved capsule with smin rounding to spare.
  layout.doors = []
  const plugFor = (edgeIdx: number, kind: DoorKind, keyId?: string) => {
    const e = edges[edgeIdx]
    const cap = layout.capsules.find(c => c.edge === edgeIdx)!
    const mx = (pos[e.a].x + pos[e.b].x) / 2
    const my = (pos[e.a].y + pos[e.b].y) / 2
    const dx = pos[e.b].x - mx
    const dy = pos[e.b].y - my
    const len = Math.hypot(dx, dy) || 1
    layout.doors!.push({
      edge: edgeIdx, kind, keyId,
      x: mx, y: my,
      r: cap.r + T.dungeon.sminK * 0.9 + 0.2,
      nx: dx / len, ny: dy / len,
      open: false,
    })
  }

  // Shortcut (B3): normal points at the deep node — the side that opens it.
  const shortcutIdx = edges.findIndex(e => e.kind === 'shortcut')
  if (shortcutIdx >= 0) plugFor(shortcutIdx, 'shortcut')

  // Gates (B4): the three deep pockets, deepest first — the hall-pass vault
  // (key from the boss), then the two adaptation seals. Their halls get
  // edge kind 'lock' so the viz and future systems can tell them apart.
  const deepPockets = graph.nodes
    .filter(n => n.role === 'reward' && (n.band ?? 0) >= 3)
    .sort((a, b) => layout.nodeDepth![b.id] - layout.nodeDepth![a.id])
  const GATE_KEYS: Array<{ kind: DoorKind; keyId: string }> = [
    { kind: 'lock', keyId: 'hall-pass' },
    { kind: 'adaptation', keyId: 'sporulation' },
    { kind: 'adaptation', keyId: 'galvanism' },
  ]
  deepPockets.slice(0, 3).forEach((pocket, i) => {
    const edgeIdx = edges.findIndex(e => e.b === pocket.id || e.a === pocket.id)
    if (edgeIdx < 0) return
    edges[edgeIdx].kind = 'lock'
    plugFor(edgeIdx, GATE_KEYS[i].kind, GATE_KEYS[i].keyId)
  })

  return layout
}

function dijkstraFromHub(graph: DunGraph, pos: Vec2[], hub: number): number[] {
  const n = graph.nodes.length
  const dist = new Array<number>(n).fill(Infinity)
  dist[hub] = 0
  const adj: Array<Array<{ to: number; w: number }>> = graph.nodes.map(() => [])
  for (const e of graph.edges) {
    // Shortcuts don't count toward depth: an escape hatch home doesn't
    // make the room behind it shallower (or safer).
    if (e.kind === 'shortcut') continue
    const w = Math.hypot(pos[e.b].x - pos[e.a].x, pos[e.b].y - pos[e.a].y)
    adj[e.a].push({ to: e.b, w })
    adj[e.b].push({ to: e.a, w })
  }
  // n ≤ ~40 — a linear-scan Dijkstra is plenty.
  const done = new Array<boolean>(n).fill(false)
  for (let k = 0; k < n; k++) {
    let u = -1
    let best = Infinity
    for (let i = 0; i < n; i++) {
      if (!done[i] && dist[i] < best) {
        best = dist[i]
        u = i
      }
    }
    if (u < 0) break
    done[u] = true
    for (const { to, w } of adj[u]) {
      if (dist[u] + w < dist[to]) dist[to] = dist[u] + w
    }
  }
  return dist
}
